import type { MessageKey } from '@ctcells/i18n'

/**
 * 사이드 패널 「현재 메트릭」 구성 — 와이어프레임 `compPanel()` 1828행의 항목 구성.
 *
 * 🔴 값은 `ModuleSnapshot` 에서 읽고, **어느 주머니인지는 `source` 가 정한다**(정정 2026-09-21).
 *    · `telemetry` (기본) — 계약이 **자유 형식**(`additionalProperties: true`)으로 두어
 *      **필드 이름을 계약이 정해 주지 않는다.** 이 표가 「무엇을 보일지」의 유일한 기록이고,
 *      서버가 그 키를 안 주면 화면은 `—` 를 낸다. 값을 지어내지 않는다.
 *    · `consumables` — 소모품 잔량. **키가 없으면 미탑재라 줄째 사라진다.**
 *    · `reagent_storage` — 계약이 구조로 선언한 시약 보관 온도 미러.
 *    🔴 **종전에는 셋을 전부 `telemetry` 에서 찾았고 소모품·온도 세 줄이 영영 `—` 였다** —
 *       서버가 그 자리에 그 칸을 둔 적이 없다. 되돌리지 마라(시험이 그 이름들을 막는다).
 *
 *    텔레메트리 키 이름은 `@ctcells/fixtures` 의 `TELEMETRY_KEYS` 와 맞췄다. 실물 서버가
 *    다른 이름을 주면 **여기 한 곳만** 고치면 된다. → docs/screens.md 「계약 공백」.
 */
export interface MetricSpec {
  key: string
  labelKey: MessageKey
  /** 스냅샷 안 필드 이름. 어느 주머니인지는 `source` 가 정한다 */
  field: string
  unit?: string
  /**
   * 값이 사는 자리. 기본은 `telemetry`.
   *
   * 🔴 **소모품은 `telemetry` 가 아니다.** 계약이 `consumables` 라는 **별 주머니**를 두었고
   *    서버가 그쪽에만 싣는다 — 팁·TC 를 `telemetry` 에서 찾던 종전 두 줄은 **영영 `—`**
   *    였다(2026-09-21 실측 · 그 칸이 `telemetry` 에 온 적이 한 번도 없다).
   * 🔴 `consumables`·`reagent_storage` 줄은 **칸이 없으면 줄째 사라진다**(미탑재).
   *    `metrics()` 가 집행한다.
   */
  /**
   * `pose` — 위치 봉투(`GET /modules` 목록의 `pose_telemetry.metrics` · v0.9.4). `field` 는 키의 «이름»(마지막 점 뒤 · `x_mm`)이다 —
   * 로봇 id(`asm.cart-1`)를 박지 않는다(`robotPoseModel.ts` 규칙). 받은 봉투가 없거나 그 이름이 없으면 칸 부재다.
   */
  source?: 'telemetry' | 'consumables' | 'reagent_storage' | 'grips' | 'tip' | 'pose'
}

/** 그림 탭 키 → 그 컴포넌트의 메트릭 */
export const METRIC_SPECS: Record<string, readonly MetricSpec[]> = {
  // 원심분리기 본체
  cent: [
    { key: 'speed', labelKey: 'metric.speed', field: 'rotor_rpm', unit: 'rpm' },
    { key: 'rcf', labelKey: 'metric.rcf', field: 'rotor_rcf', unit: '×g' },
    { key: 'temperature', labelKey: 'metric.temperature', field: 'temperature_celsius', unit: '℃' },
  ],
  /*
   * 모듈 내부 3축 로봇 — 🔴 **위치 봉투에서 읽는다**(v0.9.4 · dev2 2026-10-11 `asm.cart-1.x_mm` · `cent.cart-1.y_mm` · `stain.cart-1.z_mm` ·
   *    모의면 `pose_simulated: 1`). 종전 `telemetry.gantry_x_mm` 는 서버가 낸 적이 없는 이름이다(BR-122 — 영영 「미수신」이었다).
   *    ⚠ 같은 봉투의 `slide_mm`(슬라이드 축)은 아직 칸을 세우지 않았다 — 늘릴 때 여기 한 줄.
   *    단위는 키 접미(`_mm`)가 말한다 — `unit` 은 사람이 읽는 표기다.
   */
  gantry: [
    { key: 'x', labelKey: 'metric.axisX', field: 'x_mm', unit: 'mm', source: 'pose' },
    { key: 'y', labelKey: 'metric.axisY', field: 'y_mm', unit: 'mm', source: 'pose' },
    { key: 'z', labelKey: 'metric.axisZ', field: 'z_mm', unit: 'mm', source: 'pose' },
  ],
  // 6축 로봇 관절
  robot: [
    { key: 'j1', labelKey: 'metric.joint1', field: 'joint_1', unit: '°' },
    { key: 'j2', labelKey: 'metric.joint2', field: 'joint_2', unit: '°' },
    { key: 'j3', labelKey: 'metric.joint3', field: 'joint_3', unit: '°' },
    { key: 'j4', labelKey: 'metric.joint4', field: 'joint_4', unit: '°' },
    { key: 'j5', labelKey: 'metric.joint5', field: 'joint_5', unit: '°' },
    { key: 'j6', labelKey: 'metric.joint6', field: 'joint_6', unit: '°' },
  ],
  // 어셈블러 본체
  mod: [
    { key: 'uv', labelKey: 'metric.uv', field: 'uv_intensity_mw_cm2', unit: 'mW/cm²' },
    { key: 'tilt', labelKey: 'metric.stage', field: 'tilt_angle_deg', unit: '°' },
  ],
  // 어셈블러 TC 랙 — 🔴 `consumables` 주머니다(어셈블러만 이 칸을 선언한다)
  tc: [{ key: 'tcRack', labelKey: 'metric.tcRack', field: 'tc_remaining', source: 'consumables' }],
  // 염색기 피펫 · 시약
  pip: [
    /*
     * 🔴 칸 이름은 **`tips_remaining`(복수)** 이다. `tip_remaining` 은 서버가 내지 않는다 —
     *    module-core `_CONSUMABLE_FIELD` 가 `"tip" → "tips_remaining"` 으로 못박고 있고
     *    실물 염색기 응답도 그 이름이다(2026-09-21). 단수로 되돌리면 값이 영영 안 뜬다.
     */
    { key: 'tip', labelKey: 'metric.tip', field: 'tips_remaining', source: 'consumables' },
    /*
     * 🔴 시약 보관 온도는 **계약이 준 «구조화된» 칸**(`ModuleSnapshot.reagent_storage`)이다 —
     *    계약 설명이 「L1 온도 표시(APP-MON-04)의 계약 경로다 — **telemetry 자유형에 얹지
     *    않는다**」고 못박았는데 우리가 `telemetry.reagent_storage_celsius` 를 찾고 있었다
     *    (그런 칸은 서버에 없다 ⇒ 영영 `—`). 실물이 값을 싣기 시작했다 —
     *    `{"setpoint_celsius":4.0,"actual_celsius":10.0,"at_target":null,"state":null}`
     *    (module-core 2026-09-20 `9406f06` 로 미러가 채워졌다 · 2026-09-21 실측).
     * ⚠ 능력이 없는 모듈은 `reagent_storage: null` 이라 **줄째 사라진다.**
     */
    {
      key: 'storage',
      labelKey: 'metric.reagentStorage',
      field: 'actual_celsius',
      unit: '℃',
      source: 'reagent_storage',
    },
  ],
  // 염색기 셰이커
  shaker: [
    { key: 'shaker', labelKey: 'metric.shaker', field: 'shaker_rpm', unit: 'rpm' },
    { key: 'slider', labelKey: 'metric.slider', field: 'slider_position' },
  ],
  // 염색기 L 트레이
  tray: [{ key: 'slider', labelKey: 'metric.slider', field: 'slider_position' }],
  // 비전 — 장치 상태(MIF-VIS-03)와 검사 결과(MIF-VIS-02)는 다른 축이다. 계약에 경로가 없다
  vision: [{ key: 'verdict', labelKey: 'metric.verdict', field: 'vision_verdict' }],
}
